import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';

// Süzgeçteki "Atanmamış" seçeneği: istekte `<alan>[null]=true` olur (kademesiz / ailesiz ünvanlar).
export const MISSING = '__missing__';

// Kolon sırası — sayfa URL ön-süzgecini (?gradeMissing=1) doğru kolona yerleştirmek için kullanır.
export const TITLE_COLUMN_NAMES = ['id', 'name', 'code', 'gradeId', 'jobFamilyId'];

// "Atanmamış" süzgecini sunucu sözleşmesine çevirir: gradeId[eq]=__missing__ → gradeId[null]=true.
export const toApiParams = (params) => {
  const out = { ...params };
  ['gradeId', 'jobFamilyId'].forEach((key) => {
    if (out[`${key}[eq]`] === MISSING) {
      delete out[`${key}[eq]`];
      out[`${key}[null]`] = 'true';
    }
  });
  return out;
};

// Kayıt yükü. Güncellemede boş seçim alanı TEMİZLEMEK için açık bayrak gönderir — sunucu boş
// GradeId'yi "dokunma" sayar (eski istemci kademeyi sessizce silmesin).
export const toTitlePayload = (formData) => ({
  name: formData.name,
  code: formData.code || null,
  gradeId: formData.gradeId || null,
  jobFamilyId: formData.jobFamilyId || null,
  clearGrade: !formData.gradeId,
  clearJobFamily: !formData.jobFamilyId,
});

const listOf = (res) => {
  const r = res.data?.data;
  return Array.isArray(r) ? r : (r?.data || []);
};

const useTitleApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [grades, setGrades] = useState([]);
  const [families, setFamilies] = useState([]);
  const lastValidQueryParams = useRef(null);

  // Kademe ve aile listeleri küçüktür — süzgeç ve form seçicileri için bir kez okunur.
  useEffect(() => {
    api.get('/v1/job-grades', { params: { pageNumber: 1, pageSize: 1000 } })
      .then((res) => setGrades(listOf(res).map((g) => ({ id: g.id, name: g.name, level: g.level }))))
      .catch(() => {});
    api.get('/v1/job-families', { params: { pageNumber: 1, pageSize: 1000 } })
      .then((res) => setFamilies(listOf(res).map((f) => ({ id: f.id, name: f.name }))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/titles?${new URLSearchParams(toApiParams(paramsToUse))}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await api.post('/v1/titles', toTitlePayload(formData));
      refresh();
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, [refresh]);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await api.put(`/v1/titles/${id}`, toTitlePayload(formData));
      refresh();
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, [refresh]);

  const _delete = useCallback(async (ids) => {
    try {
      if (ids.length === 1) {
        await api.delete(`/v1/titles/${ids[0]}`);
      } else {
        await api.post('/v1/titles/batch-delete', ids);
      }
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  // Toplu kademe/aile ataması: { ids, gradeId?, jobFamilyId?, clearGrade?, clearJobFamily? }.
  const batchAssign = useCallback(async (body) => {
    try {
      const res = await api.post('/v1/titles/batch-assign', body);
      if (!res.data?.status) return false;
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const labelOf = useCallback((options) => (id) => (id === MISSING
    ? t('jobGrade.unassigned')
    : options.find((o) => o.id === id)?.name || id), [t]);

  const gradeLabel = useCallback((g) => `${g.name} (${t('jobGrade.levelShort')} ${g.level})`, [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('title.name'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'code', label: t('title.code'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'gradeId', label: t('jobGrade.grade'),
      options: {
        filter: true, sort: false, filterType: 'dropdown',
        filterOptions: { names: [MISSING, ...grades.map((g) => g.id)], defaultOp: 'eq', renderValue: labelOf(grades) },
        customFilterListOptions: { render: labelOf(grades) },
        customBodyRenderLite: (i) => data[i]?.gradeName || '-',
      },
    },
    {
      name: 'jobFamilyId', label: t('jobFamily.jobFamily'),
      options: {
        filter: true, sort: false, filterType: 'dropdown',
        filterOptions: { names: [MISSING, ...families.map((f) => f.id)], defaultOp: 'eq', renderValue: labelOf(families) },
        customFilterListOptions: { render: labelOf(families) },
        customBodyRenderLite: (i) => data[i]?.jobFamilyName || '-',
      },
    },
  ], [t, data, grades, families, labelOf]);

  const gradeAsync = useMemo(() => ({
    onSearch: async (q) => grades
      .filter((g) => !q || g.name.toLocaleLowerCase('tr').includes(q.toLocaleLowerCase('tr')))
      .map((g) => ({ id: g.id, name: gradeLabel(g) })),
    onGetById: async (id) => {
      const g = grades.find((x) => x.id === id);
      return g ? { id: g.id, name: gradeLabel(g) } : { id, name: '' };
    },
  }), [grades, gradeLabel]);

  const familyAsync = useMemo(() => ({
    onSearch: async (q) => families.filter((f) => !q || f.name.toLocaleLowerCase('tr').includes(q.toLocaleLowerCase('tr'))),
    onGetById: async (id) => families.find((f) => f.id === id) || { id, name: '' },
  }), [families]);

  const objectSchema = useMemo(() => [
    { name: 'name', label: t('title.name'), type: 'text', grid: 12, required: true },
    { name: 'code', label: t('title.code'), type: 'text', grid: 12 },
    { name: 'gradeId', label: t('jobGrade.grade'), type: 'asyncSelect', grid: 12, ...gradeAsync },
    { name: 'jobFamilyId', label: t('jobFamily.jobFamily'), type: 'asyncSelect', grid: 12, ...familyAsync },
  ], [t, gradeAsync, familyAsync]);

  return {
    objectSchema, columns, data, fetching, pagination, create, update, _delete, batchAssign,
    gradeAsync, familyAsync, refreshTrigger,
  };
};

export default useTitleApi;
